import React, { useState, useEffect, useCallback } from 'react'
import { getApiUrl, apiRequest } from '../utils/api';

const CampaignCompletionForm = ({ analysisResult, campaignData, onComplete, onCancel, onProfileInfo }) => {
  const [formData, setFormData] = useState({
    amount: '',
    views: analysisResult?.views_count || 0,
    tier: 'Tier 1',
    platform: analysisResult?.platform || 'Instagram',
    product: '',
    team_member: '',
    // Manual override fields for engagement data
    likes: analysisResult?.likes_count || 0,
    comments: analysisResult?.comments_count || 0,
    // Reshares (reposts) should start blank and be required
    reposts: '',
    // Shares should start blank and be required
    shares: '',
    // Video and caption fields
    video_url: campaignData?.video_url || '',
    caption: campaignData?.caption || '',
    video_thumbnail: campaignData?.video_thumbnail || '',
    short_code: campaignData?.short_code || ''
  });
  const [influencerInfo, setInfluencerInfo] = useState(() => {
    // Prefer campaignData.influencer_info if present (backend may attach it), else from analysisResult
    const raw = campaignData?.influencer_info || analysisResult?.influencer_info || null;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
  });
 
  // Helpers to robustly read profile fields across different API shapes
  const getNested = useCallback((obj, pathArr) => {
    try {
      return pathArr.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    } catch {
      return undefined;
    }
  }, []);

  const getUsername = useCallback((info) => (
    info?.username || info?.userName || info?.name ||
    getNested(info, ['user', 'username']) ||
    getNested(info, ['owner', 'username']) ||
    getNested(info, ['author', 'username']) ||
    getNested(info, ['graphql', 'user', 'username']) ||
    getNested(info, ['profile', 'username']) || ''
  ), [getNested]);

  const getFollowers = useCallback((info) => (
    info?.followersCount || info?.followers || info?.followerCount ||
    getNested(info, ['graphql', 'user', 'edge_followed_by', 'count']) ||
    getNested(info, ['user', 'edge_followed_by', 'count']) ||
    getNested(info, ['statistics', 'followers']) ||
    getNested(info, ['counts', 'followers']) ||
    getNested(info, ['edge_followed_by', 'count']) || undefined
  ), [getNested]);

  const getFollowing = (info) => (
    info?.followingCount || info?.following || info?.followsCount ||
    getNested(info, ['graphql', 'user', 'edge_follow', 'count']) ||
    getNested(info, ['user', 'edge_follow', 'count']) ||
    getNested(info, ['statistics', 'following']) ||
    getNested(info, ['counts', 'following']) ||
    getNested(info, ['edge_follow', 'count']) || undefined
  );

  const getPosts = (info) => (
    info?.postsCount || info?.mediaCount || info?.media_count ||
    getNested(info, ['graphql', 'user', 'edge_owner_to_timeline_media', 'count']) ||
    getNested(info, ['user', 'media', 'count']) || undefined
  );

  const getFullName = (info) => (
    info?.fullName || info?.full_name || info?.name ||
    getNested(info, ['graphql', 'user', 'full_name']) ||
    getNested(info, ['user', 'full_name']) || undefined
  );

  const getBio = (info) => (
    info?.bio || info?.biography || info?.description ||
    getNested(info, ['graphql', 'user', 'biography']) || undefined
  );

  const getExternalUrl = (info) => (
    info?.externalUrl || info?.external_url ||
    getNested(info, ['graphql', 'user', 'external_url']) || undefined
  );

  const getProfileUrl = (info) => (
    info?.url || info?.profile_url ||
    (getUsername(info) ? `https://www.instagram.com/${getUsername(info)}/` : undefined)
  );
 
  // Re-parse if parent props update with stringified influencer_info
  useEffect(() => {
    const raw = campaignData?.influencer_info || analysisResult?.influencer_info || null;
    if (!raw) { setInfluencerInfo(null); return; }
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        setInfluencerInfo(parsed);
        onProfileInfo && onProfileInfo(
          getUsername(parsed) || campaignData?.influencer_username || campaignData?.influencer_name,
          parsed
        );
      } catch {
        setInfluencerInfo(null);
      }
    } else {
      setInfluencerInfo(raw);
      onProfileInfo && onProfileInfo(
        getUsername(raw) || campaignData?.influencer_username || campaignData?.influencer_name,
        raw
      );
    }
  }, [
    campaignData?.influencer_info,
    campaignData?.influencer_name,
    campaignData?.influencer_username,
    analysisResult?.influencer_info,
    getUsername,
    onProfileInfo
  ]);

  // Build a display profile by merging normalized scraper info with fallbacks from extracted data
  const buildDisplayProfile = () => {
    // Prefer normalized influencerInfo if available
    if (influencerInfo) return influencerInfo;
    // Derive minimal from analysisResult/campaignData if scraper missing
    const derivedUsername = (
      campaignData?.influencer_username ||
      analysisResult?.ownerUsername ||
      analysisResult?.username ||
      analysisResult?.account_holder_name ||
      campaignData?.influencer_name || ''
    );
    const profileUrl = derivedUsername ? `https://www.instagram.com/${derivedUsername}/` : undefined;
    return {
      username: derivedUsername,
      fullName: analysisResult?.ownerFullName || analysisResult?.ownerName || undefined,
      followersCount: undefined,
      followingCount: undefined,
      postsCount: undefined,
      bio: analysisResult?.bio || undefined,
      externalUrl: undefined,
      url: profileUrl,
      category: analysisResult?.categoryName || analysisResult?.category || undefined
    };
  };

  const displayProfile = buildDisplayProfile();

  // Client-side fetch profile info if missing but we have a username
  useEffect(() => {
    const run = async () => {
      if (influencerInfo) return;
      const uname = getUsername(displayProfile);
      if (!uname) return;
      try {
        const resp = await fetch(`${getApiUrl()}/profile-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: uname })
        });
        const res = await resp.json();
        const info = res.data || res.profile_info;
        if (res.success && info) {
          setInfluencerInfo(info);
          onProfileInfo && onProfileInfo(uname, info);
        }
      } catch (_) {}
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayProfile && getUsername(displayProfile)]);

  const [teamMembers, setTeamMembers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTeamMembers = () => {
    apiRequest(`/team-members`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setTeamMembers(data.team_members);
        }
      })
      .catch(error => console.error('Error fetching team members:', error));
  };

  const fetchProducts = () => {
    apiRequest(`/products`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setProducts(data.products);
        }
      })
      .catch(error => console.error('Error fetching products:', error));
  };

  useEffect(() => {
    // Initial fetch
    fetchTeamMembers();
    fetchProducts();

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTeamMembers();
        fetchProducts();
      }
    };

    // Periodic refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchTeamMembers();
      fetchProducts();
    }, 30000);

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'team_members_updated') {
        fetchTeamMembers();
      }
      if (e.key === 'products_updated') {
        fetchProducts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Auto-select tier based on followers from influencer profile info
  useEffect(() => {
    if (!displayProfile) return;
    let followers = getFollowers(displayProfile);
    if (!followers) return;
    const followersNum = parseInt(followers);
    if (Number.isNaN(followersNum)) return;
    let tier = '';
    if (followersNum < 10000) tier = 'Nano';
    else if (followersNum < 50000) tier = 'Micro';
    else if (followersNum < 200000) tier = 'Mid';
    else if (followersNum < 1000000) tier = 'Macro';
    else tier = 'Mega';
    setFormData(prev => ({ ...prev, tier }));
  }, [displayProfile, getFollowers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required blank fields for shares and reshares
    const sharesTrim = (formData.shares ?? '').toString().trim();
    const repostsTrim = (formData.reposts ?? '').toString().trim();
    if (sharesTrim === '' || repostsTrim === '') {
      alert('Please enter both Shares and Reshares before submitting.');
      return;
    }
    setIsLoading(true);

    try {
      const campaignSubmissionData = {
        ...formData,
        influencer_name: analysisResult.account_holder_name,
        influencer_info: influencerInfo || null,
        likes: parseInt(formData.likes) || 0,
        comments: parseInt(formData.comments) || 0,
        reposts: parseInt(formData.reposts),
        shares: parseInt(formData.shares),
        views: parseInt(formData.views) || 0, // Use manual override views
        date: new Date().toISOString().split('T')[0],
        video_url: formData.video_url,
        caption: formData.caption,
        video_thumbnail: formData.video_thumbnail,
        post_link: campaignData?.post_link || ''
      };

      const response = await apiRequest(`/campaigns/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignSubmissionData),
      });

      const result = await response.json();

      if (result.success) {
        // Let parent handle redirect to Campaigns tab and any global success UI
        onComplete(campaignSubmissionData);
      } else {
        alert('Error creating campaign: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating campaign');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        {/* Fixed quick actions: Reel + Profile (always visible while scrolling) */}
        <div className="fixed z-40 right-4 bottom-6 md:top-20 md:right-6 md:bottom-auto">
          <div className="bg-white/95 backdrop-blur border border-gray-200 shadow-lg rounded-lg p-2 flex md:flex-row flex-col gap-2">
            {campaignData?.post_link && (
              <button
                type="button"
                onClick={() => window.open(campaignData.post_link, '_blank')}
                className="inline-flex items-center justify-center px-3 py-2 border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors"
                title="Open reel in a new tab"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Reel
              </button>
            )}
            {(() => { const profileUrl = getProfileUrl(displayProfile); return profileUrl ? (
              <button
                type="button"
                onClick={() => window.open(profileUrl, '_blank')}
                className="inline-flex items-center justify-center px-3 py-2 border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md text-sm font-medium transition-colors"
                title="Open profile in a new tab"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A7 7 0 1118.88 7.196M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Profile
              </button>
            ) : null; })()}
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900">Complete Campaign</h2>
        <p className="text-gray-600">Add additional details to complete your campaign</p>
      </div>

      {/* Influencer Account Details */}
      {displayProfile && (getUsername(displayProfile) || getBio(displayProfile) || getProfileUrl(displayProfile)) ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h4 className="text-md font-semibold text-purple-700 mb-2">Influencer Account Details</h4>
          <div className="text-sm text-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><strong>Username:</strong> {getUsername(displayProfile) || 'N/A'}</div>
              <div><strong>Full Name:</strong> {getFullName(displayProfile) || 'N/A'}</div>
              <div className="flex items-center">
                <strong>Followers:</strong> 
                <span className={`ml-1 ${getFollowers(displayProfile) ? 'text-green-600 font-semibold' : ''}`}>
                  {getFollowers(displayProfile) ? getFollowers(displayProfile).toLocaleString() : 'N/A'}
                </span>
                {getFollowers(displayProfile) && (
                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {formData.tier} Tier
                  </span>
                )}
              </div>
              <div><strong>Following:</strong> {getFollowing(displayProfile) ? getFollowing(displayProfile).toLocaleString() : 'N/A'}</div>
              <div><strong>Posts:</strong> {getPosts(displayProfile) ? getPosts(displayProfile).toLocaleString() : 'N/A'}</div>
              <div><strong>Category:</strong> {displayProfile.categoryName || displayProfile.category || 'N/A'}</div>
            </div>
            <div className="mt-2"><strong>Bio:</strong> {getBio(displayProfile) || 'N/A'}</div>
            <div className="mt-1"><strong>External URL:</strong> {(() => { const u = getExternalUrl(displayProfile); return u ? <a href={u} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{u}</a> : 'N/A'; })()}</div>
            <div className="mt-1"><strong>Profile Link:</strong> {(() => { const u = getProfileUrl(displayProfile); return u ? <a href={u} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Open</a> : 'N/A'; })()}</div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-sm text-yellow-800">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <strong>Profile details not available</strong>
          </div>
          <p className="mb-2">The profile scraper is still running or encountered an issue. This can happen if:</p>
          <ul className="list-disc list-inside mb-2 text-xs">
            <li>The Instagram profile is private</li>
            <li>The username was not found</li>
            <li>The Apify API is experiencing delays</li>
            <li>Rate limits are being applied</li>
          </ul>
          <p className="text-xs">You can still complete the campaign and set the tier manually. The profile data will be available in the campaigns list once the scraper completes.</p>
        </div>
      )}

      {/* Analysis Results Summary */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">Extracted Data</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{analysisResult?.account_holder_name || 'N/A'}</div>
            <div className="text-sm text-blue-500">Influencer</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{analysisResult?.likes_count || 0}</div>
            <div className="text-sm text-blue-500">Likes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{analysisResult?.comments_count || 0}</div>
            <div className="text-sm text-blue-500">Comments</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{campaignData?.short_code || 'N/A'}</div>
            <div className="text-sm text-blue-500">Short Code</div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Views *
                </label>
                <input
                  type="number"
                  name="views"
                  value={formData.views}
                  onChange={handleChange}
                  required
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Campaign Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform
                </label>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Instagram">Instagram</option>
                  <option value="TikTok">TikTok</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Twitter">Twitter</option>
                  <option value="LinkedIn">LinkedIn</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tier {getFollowers(displayProfile) && (
                    <span className="text-green-600 text-xs font-normal">
                      (Auto-selected based on {getFollowers(displayProfile).toLocaleString()} followers)
                    </span>
                  )}
                </label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    getFollowers(displayProfile) ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  <option value="Nano">Nano (1K-10K)</option>
                  <option value="Micro">Micro (10K-50K)</option>
                  <option value="Mid">Mid (50K-200K)</option>
                  <option value="Macro">Macro (200K-1M)</option>
                  <option value="Mega">Mega (1M+)</option>
                </select>
                {getFollowers(displayProfile) && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Tier automatically selected based on follower count
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Member *
                </label>
                <select
                  name="team_member"
                  value={formData.team_member}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select team member</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.name}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product *
                </label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.name}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Engagement Data Override */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Engagement Data 
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Edit if AI analysis is incorrect)
              </span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Likes ❤️
                </label>
                <input
                  type="number"
                  name="likes"
                  value={formData.likes}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments 💬
                </label>
                <input
                  type="number"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reshares ↗️
                </label>
                <input
                  type="number"
                  name="reposts"
                  value={formData.reposts}
                  onChange={handleChange}
                  min="0"
                  placeholder=""
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shares 📤
                </label>
                <input
                  type="number"
                  name="shares"
                  value={formData.shares}
                  onChange={handleChange}
                  min="0"
                  placeholder=""
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Views 👁️
                </label>
                <input
                  type="number"
                  name="views"
                  value={formData.views}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>💡 Tip:</strong> If the AI incorrectly counted comments as likes, set likes to 0 and enter the correct comment count.
              </p>
            </div>
          </div>

          {/* Video URL and Caption Fields */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Video Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL 🎥
                </label>
                <input
                  type="url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleChange}
                  placeholder="https://example.com/video.mp4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caption 📝
                </label>
                <textarea
                  name="caption"
                  value={formData.caption}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Enter post caption..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Video Thumbnail Preview */}
            {formData.video_thumbnail && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Thumbnail Preview 🖼️
                </label>
                <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                  <img 
                    src={formData.video_thumbnail} 
                    alt="Video thumbnail" 
                    className="max-w-xs h-auto rounded-md shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{display: 'none'}} className="text-sm text-gray-500">
                    Thumbnail not available
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Campaign'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignCompletionForm;